import { baseurl, get, LogErrorMessage, post } from '../service';

/**
 * Selected provider data for cache storage
 * @typedef {Object} SelectedProvider
 * @property {string} pwid - Provider's PWID
 * @property {string} last4 - Last 4 digits of DEA/License
 * @property {string} email - Provider's email address
 * @property {string} providerName - Provider's name
 */

/**
 * Cache item structure stored in Redis
 * @typedef {Object} CacheItem
 * @property {string} providerSelectionCacheId - Cache ID
 * @property {string} userId - User ID
 * @property {SelectedProvider[]} selectedProviders - Array of selected providers
 */

/**
 * Request payload for POST /cache endpoint
 * @typedef {Object} PostCacheRequest
 * @property {string} cacheId - Unique cache identifier (typically a GUID)
 * @property {string} userId - User identifier
 * @property {SelectedProvider[]} selectedProviders - Array of selected providers
 */

/**
 * Response from POST /cache endpoint
 * @typedef {Object} PostCacheResponse
 * @property {string} cacheId - The cache ID
 * @property {boolean} success - Whether the operation was successful
 * @property {string|null} errorMessage - Error message if any
 * @property {CacheItem|null} cacheItem - Cache item data (usually null on POST)
 */

/**
 * Response from GET /cache/{cacheId} endpoint
 * @typedef {Object} GetCacheResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {string|null} errorMessage - Error message if any
 * @property {CacheItem|null} cacheItem - The cached data
 * @property {string} cacheId - The cache ID
 * @property {boolean} found - Whether the cache item was found
 */

/** @type {PostCacheResponse} */
const POST_ERROR_RESPONSE = {
  cacheId: '',
  success: false,
  errorMessage: 'An error occurred while storing providers in cache',
  cacheItem: null,
};

/**
 * Store provider selections in Redis cache
 * @param {string} cacheId - Unique cache identifier (typically a GUID)
 * @param {string} userId - User identifier
 * @param {SelectedProvider[]} selectedProviders - Array of selected providers
 * @returns {Promise<PostCacheResponse>}
 */
export const storeProvidersInCache = async (
  cacheId,
  userId,
  selectedProviders,
) => {
  if (!cacheId || !userId) {
    console.error('storeProvidersInCache: cacheId and userId are required', {
      cacheId,
      userId,
    });
    return {
      ...POST_ERROR_RESPONSE,
      cacheId,
      errorMessage: 'cacheId and userId are required',
    };
  }

  if (!Array.isArray(selectedProviders) || selectedProviders.length === 0) {
    console.error(
      'storeProvidersInCache: selectedProviders must be a non-empty array',
      { selectedProviders },
    );
    return {
      ...POST_ERROR_RESPONSE,
      cacheId,
      errorMessage: 'selectedProviders must be a non-empty array',
    };
  }

  try {
    const url = `${baseurl()}/cache`;

    /** @type {PostCacheRequest} */
    const payload = {
      cacheId,
      userId,
      selectedProviders,
    };

    /** @type {PostCacheResponse} */
    const response = await post(url, payload, '2.0');

    return response;
  } catch (error) {
    console.error('Failed to store providers in cache:', error);
    LogErrorMessage(error);
    return {
      ...POST_ERROR_RESPONSE,
      cacheId,
      errorMessage: 'Unable to proceed to checkout. Please try again.',
    };
  }
};

/** @type {GetCacheResponse} */
const GET_ERROR_RESPONSE = {
  success: false,
  errorMessage: 'An error occurred while retrieving providers from cache',
  cacheItem: null,
  cacheId: '',
  found: false,
};

/**
 * Retrieve provider selections from Redis cache
 * @param {string} cacheId - Unique cache identifier
 * @returns {Promise<GetCacheResponse>} Cache response or null if error
 */
export const getProvidersFromCache = async (cacheId) => {
  if (!cacheId) {
    console.error('getProvidersFromCache: cacheId is required');
    return {
      ...GET_ERROR_RESPONSE,
      cacheId,
      errorMessage: 'cacheId is required',
    };
  }

  try {
    const url = `${baseurl()}/cache/${encodeURIComponent(cacheId)}`;

    /** @type {GetCacheResponse} */
    const response = await get(url, '2.0');

    return response;
  } catch (error) {
    console.error('Failed to retrieve providers from cache:', error);
    LogErrorMessage(error);

    return { ...GET_ERROR_RESPONSE, cacheId, errorMessage: error.message };
  }
};
